import React, { Component } from 'react';
import { Field } from 'formik';
import { Form, Label } from 'semantic-ui-react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';
import * as Formatters from '../../../utils/formatters';

import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import StyledDatepicker, { StyledIcon } from './Datepicker.style';

export class renderField extends Component {
  state = {
    isOpen: false,
    selected: null
  };

  componentDidUpdate() {
    const { selected } = this.state;
    const {
      field: { value }
    } = this.props;
    // when value is initially pre-populated, manually select the calendar
    if (selected === null && value) {
      this.setState({ selected: new Date(value) });
    }
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOnBlur);
  }

  // for blur on outside the component
  handleOnBlur = e => {
    if (
      this.calendar &&
      !this.calendar.contains(e.target) &&
      this.state.isOpen
    ) {
      this.setState({ isOpen: false });
    }
  };
  // for focus on input field
  handleOnFocus = () => {
    this.setState({ isOpen: true });
  };

  // for change on calendar
  handleOnChange = (selected, modifiers = {}) => {
    // when the disabled date is clicked
    if (modifiers.disabled) {
      return;
    }

    const {
      field: { name },
      form: { setFieldValue }
    } = this.props;
    setFieldValue(name, selected);

    this.setState({ isOpen: false, selected });
  };

  render() {
    const {
      field,
      form: { dirty, touched, errors, setFieldValue },
      className,
      validateOnChange = false,
      fromMonth,
      toMonth,
      disabledDays,
      onDayClick,
      ...rest
    } = this.props;
    const { name } = field;
    const value = field.value || '';

    // remove `defaultToToday` from rest to avoid error on Input
    const refinedRest = Object.keys(rest).reduce((result, key) => {
      if (key !== 'defaultToToday') {
        result[key] = rest[key];
      }
      return result;
    }, {});

    // In some cases we want to show validation errors immediately after the field
    // has been changed (vs. on blur)
    const touchedOrDirty = validateOnChange ? dirty : touched[name];

    // adding input class to reuse bulma
    const classes = classnames(className, 'input', {
      'is-danger': touchedOrDirty && errors[name]
    });

    // If the DatePicker has set a value from user beofre, the default month will be the chosen month
    const initialMonth = this.state.selected
      ? moment(this.state.selected).toDate()
      : moment().toDate();
    // querySelector does not work with dots(.) - for e2e
    const id = name && name.replace('.', '_');
    return (
      <div className={'input__wrapper'}>
        <StyledIcon
          name="calendar alternate outline"
          className="datepicker__icon"
        />
        {value && (
          <StyledIcon
            name="remove circle"
            className="datepicker__icon"
            onClick={() => setFieldValue(name, '')}
          />
        )}
        <input
          type="text"
          id={id}
          name={name}
          value={Formatters.date(value)}
          className={classes}
          onFocus={this.handleOnFocus}
          {...refinedRest}
          autoComplete="off"
          readOnly
        />
        {touchedOrDirty && errors[name] && (
          <Label basic pointing color="red">
            {errors[name]}
          </Label>
        )}
        <StyledDatepicker
          ref={calendar => (this.calendar = calendar)}
          className={classnames(
            'datepicker',
            this.state.isOpen && 'datepicker--active'
          )}
        >
          <DayPicker
            month={initialMonth}
            selectedDays={this.state.selected}
            onDayClick={(selected, modifiers = {}) => {
              this.handleOnChange(selected, modifiers);
              // custom onDayClick handle
              onDayClick && onDayClick();
            }}
            fromMonth={fromMonth}
            toMonth={toMonth}
            disabledDays={disabledDays}
            fixedWeeks
          />
        </StyledDatepicker>
      </div>
    );
  }
}

renderField.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  className: PropTypes.string,
  validateOnChange: PropTypes.bool,
  fromMonth: PropTypes.object,
  toMonth: PropTypes.object,
  disabledDays: PropTypes.array,
  defaultToToday: PropTypes.bool,
  onDayClick: PropTypes.func
};
renderField.defaultProps = {
  disabledDays: [],
  defaultToToday: false
};

const Datepicker = props => {
  const { component = renderField, label, validate, name, ...rest } = props;

  return (
    <Form.Field>
      {label && <label htmlFor={name}>{label}</label>}

      <Field {...rest} name={name} validate={validate} component={component} />
    </Form.Field>
  );
};

Datepicker.propTypes = {
  component: PropTypes.func,
  className: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  name: PropTypes.string.isRequired,
  validate: PropTypes.arrayOf(PropTypes.func)
};

export default Datepicker;
